import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

const VARIANT_STYLES = {
  primary:   'bg-gradient-to-r from-[#4f8ff8] to-[#6366f1] text-white border-transparent hover:opacity-90 shadow-sm',
  secondary: 'bg-white dark:bg-[#141e30] text-gray-700 dark:text-[#d5deea] border-gray-200 dark:border-[#1d2d47] hover:bg-gray-50 dark:hover:bg-[#1a2744]',
  ghost:     'bg-transparent text-gray-600 dark:text-[#c2d0e0] border-transparent hover:bg-gray-100 dark:hover:bg-[#141e30] hover:text-gray-900 dark:hover:text-[#f1f5fb]',
  danger:    'bg-red-50 dark:bg-[#f5476a]/10 text-red-600 dark:text-[#f5476a] border-red-200 dark:border-[#f5476a]/30 hover:bg-red-100 dark:hover:bg-[#f5476a]/20',
};

const SIZE_STYLES = {
  sm: 'px-3 py-1.5 text-[12px] rounded-lg gap-1.5',
  md: 'px-4 py-2.5 text-[13px] rounded-xl gap-2',
  lg: 'px-5 py-3   text-[14px] rounded-xl gap-2.5',
};

export default function Button({
  variant = 'primary',
  size    = 'md',
  loading = false,
  icon,
  children,
  className = '',
  disabled,
  ...props
}: ButtonProps) {
  const isDisabled = disabled || loading;

  return (
    <button
      disabled={isDisabled}
      className={`
        inline-flex items-center justify-center font-semibold
        border transition-all duration-200 cursor-pointer
        active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4f8ff8]/50
        disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100
        ${VARIANT_STYLES[variant]}
        ${SIZE_STYLES[size]}
        ${className}
      `}
      {...props}
    >
      {loading ? (
        <svg className="w-4 h-4 animate-spin shrink-0" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a10 10 0 100 10 10 10 0 000-10z" />
        </svg>
      ) : icon ? (
        <span className="shrink-0 w-4 h-4 flex items-center justify-center">{icon}</span>
      ) : null}
      {children}
    </button>
  );
}
